import React, { useCallback, useEffect, useMemo, useRef } from "react";
import { apiDomain } from "configs/configs";
import { logout } from "helpers/helpers";
import { EyeInvisibleOutlined, EyeTwoTone } from "@ant-design/icons";
import {
  Select,
  Flex,
  Input,
  InputNumber,
  Checkbox,
  Form,
  Row,
  Col,
  Typography,
  Button,
  notification,
  Card,
  Grid,
} from "antd";
import styles from "pages/admin/masters/masters.module.scss";
import clsx from "clsx";
import MobileInnerWrapper from "layouts/mobileInnerWrapper/mobileInnerWrapper";
import Table, { ColumnsType } from "components/table/table";
import { useParams, useNavigate } from "react-router-dom";
import useMastersStore, { User, Citizenship } from "stores/admin/masters";

type Role = {
  id: "master" | "senior_master" | "new_master";
  name: string;
};

type ComponentProps = {};

const { Text, Title } = Typography;
const { useBreakpoint } = Grid;

const Page: React.FC<ComponentProps> = () => {
  const { "*": subRoute } = useParams();
  const navigate = useNavigate();

  const isAddMode = useMemo(() => {
    if (subRoute === "add") return true;

    return false;
  }, [subRoute]);

  const isEditMode = useMemo(() => {
    if (!subRoute?.startsWith("edit/")) return false;

    const id = parseInt(subRoute.split("/")[1], 10);

    return isNaN(id) ? false : id;
  }, [subRoute]);

  const {
    list,
    setList,
    putIds,
    setPutIds,
    isPostInProcess,
    setIsPostInProcess,
    nameFilter,
    setNameFilter,
    statusFilter,
    setStatusFilter,
    citizenshipsList,
    setCitizenshipsList,
    isSubmittable,
    setIsSubmittable,
    isError,
    setIsError,
  } = useMastersStore((store) => store);

  const [form] = Form.useForm();
  const values = Form.useWatch([], form);
  const screens = useBreakpoint();
  const isMobile = !screens.lg;

  const listRef = useRef(list);

  //  Error while saving change status
  const openNotificationWithError = useCallback(
    ({ message }: { message: string }) => {
      notification.error({
        message: "Ошибка",
        description: message,
        placement: "topRight",
        duration: 3,
      });
    },
    []
  );

  //  Save current list to the ref so we can use it in useEffect without trigger it
  useEffect(() => {
    listRef.current = list;
  }, [list]);

  //  Effect triggers when mode changes to edit or add. Also when some value changes. It fires validation and reset error.
  useEffect(() => {
    if (!isAddMode && !isEditMode) return;

    setIsError(false);

    form
      .validateFields({ validateOnly: true })
      .then(() => setIsSubmittable(true))
      .catch(() => setIsSubmittable(false));
  }, [isAddMode, isEditMode, form, values, setIsError, setIsSubmittable]);

  //  Get all users
  const getList = useCallback(
    async (isRefresh?: boolean) => {
      const fetchData = async () => {
        try {
          const url = `${apiDomain}/api/records/users`;
          const options: RequestInit = {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
            },
            credentials: "include",
          };

          const response = await fetch(url, options);

          if (!response.ok) throw new Error();

          const data = await response.json();

          if (!data?.records) throw new Error();

          const records: User[] = data.records.filter(
            (record: User) =>
              !!["new_master", "senior_master", "master"].includes(record.role)
          );

          setList({ isLoaded: true, data: records });
        } catch (error) {
          logout();
        }
      };

      if (list.isLoaded && !isRefresh) return;

      await fetchData();
    },
    [setList, list]
  );

  //  Get all citizenships
  const getCitizenshipsList = useCallback(async () => {
    const fetchData = async () => {
      try {
        const url = `${apiDomain}/api/records/citizenships`;
        const options: RequestInit = {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include",
        };

        const response = await fetch(url, options);

        if (!response.ok) throw new Error();

        const data = await response.json();

        if (!data?.records) throw new Error();

        const records: Citizenship[] = data.records;

        setCitizenshipsList({ isLoaded: true, data: records });
      } catch (error) {
        logout();
      }
    };

    if (citizenshipsList.isLoaded) return;

    await fetchData();
  }, [setCitizenshipsList, citizenshipsList]);

  //  When started, get all data
  useEffect(() => {
    getList();
    getCitizenshipsList();
  }, [getList, getCitizenshipsList]);

  //  Edit button sets edit mode or reset it
  const handleEditButton = useCallback(
    (id?: number) => {
      navigate(`/masters/edit/${id}`);
    },
    [navigate]
  );

  //  Triggers when isEditMode changed. Set initial fields value in form or reset it. Reset error.
  useEffect(() => {
    const item = listRef.current.data.find((item) => item.id === isEditMode);

    if (!item) form.resetFields();

    if (item)
      form.setFieldsValue({
        name: item.name,
        role: item.role,
        hourly_rate: item.hourly_rate,
        phone: item.phone.startsWith("+7 ") ? item.phone.slice(3) : item.phone,
        newPassword: "",
        place_of_birth: item.place_of_birth,
        email: item.email,
        citizenship_id: item.citizenship_id,
        is_self_employed: !!item.is_self_employed,
        comment: item.comment,
        status: item.status === "active",
      });

    setIsError(false);

    form
      .validateFields({ validateOnly: true })
      .then(() => setIsSubmittable(true))
      .catch(() => setIsSubmittable(false));
  }, [isEditMode, form, list.isLoaded, setIsError, setIsSubmittable]);

  //  Add button sets add mode or reset it
  const handleAddButton = useCallback(() => {
    navigate(`/masters/add`);
  }, [navigate]);

  const handleBackButton = useCallback(() => {
    navigate(`/masters`);
  }, [navigate]);

  const isInProcess = useMemo(() => {
    if (!!isAddMode && !!isPostInProcess) return true;

    if (!!isEditMode && !!putIds.includes(isEditMode)) return true;

    return false;
  }, [isAddMode, isPostInProcess, isEditMode, putIds]);

  //  Submit form, depends of mode
  const handleFinish = useCallback(() => {
    if (isInProcess) return;

    const fetchData = async () => {
      setIsError(false);

      const editModeId = isEditMode;

      if (editModeId)
        setPutIds((state) => {
          return [...state, editModeId];
        });

      if (isAddMode) setIsPostInProcess(true);

      const url = editModeId
        ? `${apiDomain}/api/records/users/${editModeId}`
        : `${apiDomain}/api/records/users`;

      const options: RequestInit = {
        method: editModeId ? "PUT" : "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          name: values.name.trim(),
          phone: values.phone
            .replace(/\D/g, "")
            .replace(/(\d{3})(\d{3})(\d{2})(\d{2})/, "+7 $1 $2-$3-$4"),
          password: values.newPassword,
          role: values.role,
          hourly_rate: values.hourly_rate ? values.hourly_rate : 0,
          place_of_birth: values.place_of_birth,
          email: values.email,
          is_self_employed: !!values.is_self_employed,
          comment: values.comment,
          citizenship_id: values.citizenship_id,
          status: values.status ? "active" : "unactive",
        }),
        credentials: "include",
      };

      try {
        const response = await fetch(url, options);

        if (!response.ok) {
          const errorData = await response.json();

          const errorText: { [key in typeof errorCode]: string } = {
            citizenshipIdNotFound: "Гражданство не найдено",
            phoneInvalid: "Неверный формат телефона",
            phoneAlreadyExists:
              "Этот номер телефона уже используется другим пользователем",
            emailInvalid: "Неверный формат email",
            emailAlreadyExists: "Этот email уже используется",
            passwordEmpty: "Пароль не может быть пустым",
          };

          const errorFields: string[] = errorData.fields;
          const errorCode:
            | "citizenshipIdNotFound"
            | "phoneInvalid"
            | "phoneAlreadyExists"
            | "emailInvalid"
            | "emailAlreadyExists"
            | "passwordEmpty" = errorData.errorCode;

          if (errorFields.length) {
            const errorMessage = errorText[errorCode];

            errorFields.forEach((errorField) => {
              form.setFields([
                {
                  name: errorField,
                  errors: [errorMessage],
                },
              ]);
            });
          }

          throw new Error();
        }

        getList(true);

        form.resetFields();
        form
          .validateFields({ validateOnly: true })
          .then(() => setIsSubmittable(true))
          .catch(() => setIsSubmittable(false));

        navigate(`/masters`);

        if (editModeId)
          setPutIds((state) => {
            return state.filter((id) => id !== editModeId);
          });

        if (isAddMode) setIsPostInProcess(false);
      } catch (error) {
        setIsError(true);

        if (editModeId)
          setPutIds((state) => {
            return state.filter((id) => id !== editModeId);
          });

        if (isAddMode) setIsPostInProcess(false);
      }
    };

    fetchData();
  }, [
    isEditMode,
    isAddMode,
    form,
    values,
    isInProcess,
    navigate,
    setIsError,
    getList,
    setIsPostInProcess,
    setPutIds,
    setIsSubmittable,
  ]);

  const handlePhoneBlur = useCallback(() => {
    const phoneValue = form.getFieldValue("phone");
    if (!phoneValue) return;

    const digitsOnly = phoneValue.replace(/\D/g, "");
    if (digitsOnly.length !== 10) return;

    const formattedPhone = digitsOnly.replace(
      /(\d{3})(\d{3})(\d{2})(\d{2})/,
      "$1 $2-$3-$4"
    );
    form.setFieldsValue({ phone: formattedPhone });
  }, [form]);

  //  Change status request
  const handleChangeStatus = useCallback(
    (id: number) => {
      const currentItem = list.data.find((item) => item.id === id);

      if (!currentItem) return;

      setPutIds((state) => {
        return [...state, currentItem.id];
      });

      const fetchData = async () => {
        const url = `${apiDomain}/api/records/users/${currentItem.id}`;
        const options: RequestInit = {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            status: currentItem.status === "active" ? "unactive" : "active",
          }),
          credentials: "include",
        };

        try {
          const response = await fetch(url, options);

          if (!response.ok) throw new Error();

          await getList(true);

          setPutIds((state) => {
            return state.filter((id) => id !== currentItem.id);
          });
        } catch (error) {
          setPutIds((state) => {
            return state.filter((id) => id !== currentItem.id);
          });

          openNotificationWithError({
            message: "Ошибка при изменении статуса",
          });
        }
      };

      fetchData();
    },
    [list, getList, setPutIds, openNotificationWithError]
  );

  //  Set columns for table
  const columns: ColumnsType = useMemo(() => {
    const result: ColumnsType = [
      {
        title: "id",
        key: "id",
        render: (text: any, untypedRecord: unknown) => {
          const record = untypedRecord as User;
          const recordStyles = record.styles;

          return (
            <div
              className={clsx(
                record.status === "unactive" && recordStyles?.disabledRow
              )}
            >
              {record.id}
            </div>
          );
        },
      },
      {
        title: "Имя",
        key: "name",
        width: "33%",
        render: (text: any, untypedRecord: unknown) => {
          const record = untypedRecord as User;
          const recordStyles = record.styles;

          return (
            <div
              className={clsx(
                record.status === "unactive" && recordStyles?.disabledRow
              )}
            >
              {record.name}
            </div>
          );
        },
      },
      {
        title: "Роль",
        key: "role",
        width: "33%",
        render: (text: any, untypedRecord: unknown) => {
          const record = untypedRecord as User;
          const recordStyles = record.styles;

          return (
            <div
              className={clsx(
                record.status === "unactive" && recordStyles?.disabledRow
              )}
            >
              {record.role === "new_master"
                ? "Новый мастер"
                : record.role === "senior_master"
                ? "Старший мастер"
                : "Мастер"}
            </div>
          );
        },
      },
      {
        title: "Ставка в час",
        key: "hourly_rate",
        width: "33%",
        render: (text: any, untypedRecord: unknown) => {
          const record = untypedRecord as User;
          const recordStyles = record.styles;

          return (
            <div
              className={clsx(
                record.status === "unactive" && recordStyles?.disabledRow
              )}
            >
              {record.hourly_rate}
            </div>
          );
        },
      },
      {
        key: "edit",
        render: (text: any, untypedRecord: unknown) => {
          const record = untypedRecord as User;
          const recordStyles = record.styles;

          return (
            <>
              <Button
                onClick={() => handleEditButton(record.id)}
                type={record.status === "active" ? "primary" : "default"}
                className={recordStyles?.actionButton}
                disabled={isInProcess}
              >
                Изменить
              </Button>
            </>
          );
        },
      },
      {
        key: "changeStatus",
        render: (text: any, untypedRecord: unknown) => {
          const record = untypedRecord as User;
          const recordStyles = record.styles;

          return (
            <>
              <Button
                onClick={() => handleChangeStatus(record.id)}
                type={record.status === "active" ? "primary" : "default"}
                danger={record.status === "active"}
                loading={putIds.includes(record.id)}
                className={recordStyles?.actionButton}
                disabled={isInProcess}
              >
                {record.status === "active" ? "Выключить" : "Включить"}
              </Button>
            </>
          );
        },
      },
    ];

    return result;
  }, [putIds, isInProcess, handleEditButton, handleChangeStatus]);

  const rolesList: Role[] = useMemo(() => {
    const result: Role[] = [
      { id: "new_master", name: "Новый мастер" },
      { id: "senior_master", name: "Старший мастер" },
      { id: "master", name: "Мастер" },
    ];

    return result;
  }, []);

  return (
    <MobileInnerWrapper>
      <Row justify="space-between" align="middle" className={styles.titleBlock}>
        <Col>
          <Title level={3} className={styles.title}>
            {isAddMode
              ? `Добавление мастера`
              : isEditMode
              ? `Редактирование мастера №${isEditMode}`
              : `Мастера`}
          </Title>
        </Col>
        <Col>
          <Button
            type="primary"
            onClick={
              isAddMode || isEditMode ? handleBackButton : handleAddButton
            }
            disabled={isInProcess}
          >
            {isAddMode || isEditMode
              ? `Вернуться к списку`
              : `Добавить мастера`}
          </Button>
        </Col>
      </Row>
      {(!!isAddMode || !!isEditMode) &&
        !!citizenshipsList.isLoaded &&
        ((!!isAddMode &&
          !citizenshipsList.data.filter(
            (citizenship) => citizenship.status === "active"
          ).length) ||
          (!!isEditMode && !citizenshipsList.data.length)) && (
          <Typography>
            <Text type="danger">
              Для {isEditMode ? "редактирования" : "создания"} мастера
              необходимо добавить гражданство в список гражданств.
            </Text>
          </Typography>
        )}
      {(!!isAddMode || !!isEditMode) &&
        !(
          (!!isAddMode &&
            !citizenshipsList.data.filter(
              (citizenship) => citizenship.status === "active"
            ).length) ||
          (!!isEditMode && !citizenshipsList.data.length)
        ) && (
          <>
            <Flex vertical gap={16}>
              <Form
                layout={"vertical"}
                form={form}
                onFinish={handleFinish}
                initialValues={{
                  name: "",
                  role: undefined,
                  hourly_rate: "",
                  phone: "",
                  newPassword: "",
                  place_of_birth: "",
                  email: "",
                  citizenship_id: undefined,
                  is_self_employed: false,
                  comment: "",
                  status: true,
                }}
              >
                <Form.Item
                  name="name"
                  label="Имя мастера"
                  rules={[
                    {
                      required: true,
                      message: "Пожалуйста, введите имя мастера!",
                    },
                  ]}
                  validateTrigger={false}
                  required={false}
                >
                  <Input
                    size="large"
                    name="name"
                    disabled={isInProcess || !list.isLoaded}
                  />
                </Form.Item>
                <Form.Item
                  name="role"
                  label="Роль"
                  rules={[
                    {
                      required: true,
                      message: "Пожалуйста, выберите роль!",
                    },
                  ]}
                  validateTrigger={false}
                  required={false}
                >
                  <Select
                    placeholder="Выберите роль"
                    disabled={isInProcess || !list.isLoaded}
                  >
                    {rolesList.map((role) => {
                      return (
                        <Select.Option key={role.id} value={role.id}>
                          {role.name}
                        </Select.Option>
                      );
                    })}
                  </Select>
                </Form.Item>
                <Form.Item
                  name="hourly_rate"
                  label="Ставка в час"
                  rules={[
                    {
                      required: false,
                      message: "Пожалуйста, введите ставку!",
                    },
                  ]}
                  validateTrigger={false}
                  required={false}
                >
                  <InputNumber
                    size="large"
                    step={1}
                    min={0}
                    name="hourly_rate"
                    disabled={isInProcess || !list.isLoaded}
                  />
                </Form.Item>
                <Form.Item
                  name="phone"
                  label="Телефон"
                  rules={[
                    {
                      required: true,
                      message: "Пожалуйста, введите телефон!",
                    },
                  ]}
                  validateTrigger={false}
                  required={false}
                >
                  <Input
                    addonBefore="+7"
                    size="large"
                    name="phone"
                    onBlur={handlePhoneBlur}
                    disabled={isInProcess || !list.isLoaded}
                  />
                </Form.Item>
                <Form.Item
                  name="newPassword"
                  label="Пароль"
                  rules={[
                    {
                      required: isEditMode ? false : true,
                      message: "Пожалуйста, введите пароль!",
                    },
                  ]}
                  validateTrigger={false}
                  required={false}
                >
                  <Input.Password
                    size="large"
                    name="newPassword"
                    iconRender={(visible) =>
                      visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />
                    }
                    disabled={isInProcess || !list.isLoaded}
                  />
                </Form.Item>
                <Form.Item
                  name="place_of_birth"
                  label="Место рождения"
                  rules={[
                    {
                      required: false,
                      message: "Пожалуйста, введите место рождения!",
                    },
                  ]}
                  validateTrigger={false}
                  required={false}
                >
                  <Input
                    size="large"
                    name="place_of_birth"
                    disabled={isInProcess || !list.isLoaded}
                  />
                </Form.Item>
                <Form.Item
                  name="email"
                  label="Эл. адрес"
                  validateTrigger={false}
                  required={false}
                >
                  <Input
                    size="large"
                    name="email"
                    disabled={isInProcess || !list.isLoaded}
                  />
                </Form.Item>
                <Form.Item
                  name="citizenship_id"
                  label="Гражданство"
                  rules={[
                    {
                      required: true,
                      message: "Пожалуйста, выберите гражданство!",
                    },
                  ]}
                  validateTrigger={false}
                  required={false}
                >
                  <Select
                    placeholder="Выберите гражданство"
                    disabled={isInProcess || !list.isLoaded}
                  >
                    {citizenshipsList.data.map((citizenship) => {
                      return (
                        <Select.Option
                          key={citizenship.id}
                          value={citizenship.id}
                          disabled={citizenship.status !== "active"}
                        >
                          {citizenship.name}
                        </Select.Option>
                      );
                    })}
                  </Select>
                </Form.Item>
                <Form.Item name="is_self_employed" valuePropName="checked">
                  <Checkbox disabled={isInProcess || !list.isLoaded}>
                    Самозанятый
                  </Checkbox>
                </Form.Item>
                <Form.Item
                  name="comment"
                  label="Комментарий"
                  rules={[
                    {
                      required: false,
                      message: "Пожалуйста, введите комментарий!",
                    },
                  ]}
                  validateTrigger={false}
                  required={false}
                >
                  <Input
                    size="large"
                    name="comment"
                    disabled={isInProcess || !list.isLoaded}
                  />
                </Form.Item>
                <Form.Item name="status" valuePropName="checked">
                  <Checkbox disabled={isInProcess || !list.isLoaded}>
                    Мастер включен
                  </Checkbox>
                </Form.Item>
                <Form.Item>
                  <Button
                    type="primary"
                    size="large"
                    className={styles.button}
                    disabled={!isSubmittable || !list.isLoaded}
                    htmlType="submit"
                    loading={isInProcess}
                  >
                    Сохранить
                  </Button>
                </Form.Item>
                {!!isError && (
                  <Form.Item>
                    <Text type="danger">Не удалось сохранить мастера</Text>
                  </Form.Item>
                )}
              </Form>
            </Flex>
          </>
        )}
      {!isAddMode && !isEditMode && (
        <>
          <Card
            className={styles.filters}
            classNames={{
              body: isMobile ? styles.mobileFiltersBody : undefined,
            }}
          >
            <Input
              placeholder="Фильтр по имени"
              value={nameFilter}
              onChange={(event) => setNameFilter(event.target.value)}
              className={clsx(
                styles.nameFilter,
                !!isMobile && styles.mobileNameFilter
              )}
              disabled={isInProcess || !list.isLoaded}
            />
            <Checkbox
              onChange={(event) =>
                setStatusFilter(event.target.checked ? "active" : "unactive")
              }
              checked={statusFilter === "active"}
              disabled={isInProcess || !list.isLoaded}
            >
              Статус: активен
            </Checkbox>
          </Card>
          <Table
            dataSource={list.data.filter((listItem) => {
              if (statusFilter === "active")
                if (listItem.status !== "active") return false;

              if (nameFilter)
                if (
                  listItem.name &&
                  !listItem.name
                    .toLowerCase()
                    .includes(nameFilter.toLowerCase())
                )
                  return false;

              return true;
            })}
            columns={columns}
            rowKey="id"
            pagination={false}
            loading={!list.isLoaded}
            styles={styles}
          />
        </>
      )}
    </MobileInnerWrapper>
  );
};

export default Page;
